import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, User, ChevronDown } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import logo from '../asset/new.png';

// Cowrie Shell SVG for decorative elements
const CowrieShell = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="#FFD700"
    stroke="#C0392B"
    strokeWidth="1"
    className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
  >
    <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm0 16c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7z" />
    <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z" />
  </svg>
);

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, currentUser, logout } = useAuth();

  // Track scrolling for header style changes
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  const handleDashboardClick = () => {
    navigate('/admin');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Features', href: '/#features' },
    { name: 'How It Works', href: '/#steps' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 font-inter ${
        isScrolled || isMobileMenuOpen
          ? 'bg-[#FFE8D6] text-[#1A3C34] shadow-md py-2'
          : 'bg-transparent text-[#FFE8D6] py-4 backdrop-blur-sm'
      }`}
    >
      {/* Subtle Adinkra pattern when not scrolled */}
      {!isScrolled && !isMobileMenuOpen && (
        <div
          className="absolute inset-0 bg-repeat opacity-10"
          style={{
            backgroundImage: `url('https://www.transparenttextures.com/patterns/kale-salad.png')`,
          }}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center group relative">
              <div className="flex items-center justify-center transition-colors duration-300">
                <img
                  src={logo}
                  alt="TaleOfTwo Logo"
                  className="h-10 w-auto object-contain"
                  onError={(e) => (e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7')}
                />
                <CowrieShell />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isScrolled
                    ? 'text-[#1A3C34] hover:text-[#C0392B]'
                    : 'text-[#FFE8D6] hover:text-[#FFD700]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons / User Profile */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full group relative ${
                    isScrolled
                      ? 'bg-[#FFF8E7] hover:bg-[#E67E22]/20 text-[#1A3C34]'
                      : 'bg-[#C0392B]/20 hover:bg-[#C0392B]/30 text-[#FFE8D6] backdrop-blur-sm'
                  } transition-colors border border-[#FFD700]/50`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span className="font-medium">{currentUser?.firstName || 'User'}</span>
                  <ChevronDown className="h-4 w-4" />
                  <CowrieShell />
                </button>

                {/* Dropdown menu */}
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-48 bg-[#FFE8D6] rounded-md shadow-lg py-1 z-10 border border-[#FFD700]/50"
                  >
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-sm text-[#1A3C34] hover:bg-[#E67E22]/20 hover:text-[#C0392B]"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/admin/couple-settings"
                      className="block px-4 py-2 text-sm text-[#1A3C34] hover:bg-[#E67E22]/20 hover:text-[#C0392B]"
                    >
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-[#1A3C34] hover:bg-[#E67E22]/20 hover:text-[#C0392B] border-t border-[#FFD700]/50"
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={handleLoginClick}
                  className={`px-4 py-2 text-sm font-medium rounded-md group relative ${
                    isScrolled
                      ? 'text-[#1A3C34] hover:text-[#C0392B]'
                      : 'text-[#FFE8D6] hover:text-[#FFD700]'
                  } transition-colors`}
                >
                  Log in
                  <CowrieShell />
                </button>

                <button
                  onClick={handleRegisterClick}
                  className={`px-5 py-2 text-sm font-medium rounded-full group relative ${
                    isScrolled
                      ? 'bg-gradient-to-r from-[#C0392B] to-[#E67E22] text-white hover:from-[#E67E22] hover:to-[#C0392B]'
                      : 'bg-[#FFE8D6] text-[#C0392B] hover:bg-[#FFD700]'
                  } transition-colors shadow-sm border border-[#FFD700]/50`}
                >
                  Sign up free
                  <CowrieShell />
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md focus:outline-none group relative ${
                isScrolled || isMobileMenuOpen
                  ? 'text-[#1A3C34] hover:bg-[#E67E22]/20'
                  : 'text-[#FFE8D6] hover:bg-[#C0392B]/20'
              }`}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
              <CowrieShell />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden bg-[#FFE8D6] border-t border-[#FFD700]/50 shadow-lg"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block px-3 py-2 rounded-md text-base font-medium text-[#1A3C34] hover:text-[#C0392B] hover:bg-[#E67E22]/20"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="pt-4 pb-3 border-t border-[#FFD700]/50">
            {isAuthenticated ? (
              <div className="flex items-center px-5">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-[#E67E22]/20 flex items-center justify-center">
                    <User className="h-6 w-6 text-[#C0392B]" />
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-[#1A3C34]">
                    {currentUser?.firstName} {currentUser?.lastName}
                  </div>
                  <div className="text-sm font-medium text-[#1A3C34]/80">
                    {currentUser?.email}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-around mt-3 px-2 space-y-1">
                <button
                  onClick={handleLoginClick}
                  className="w-full mx-2 block px-3 py-2 rounded-md text-base font-medium text-[#1A3C34] hover:text-[#C0392B] hover:bg-[#E67E22]/20 text-center group relative"
                >
                  Log in
                  <CowrieShell />
                </button>
                <button
                  onClick={handleRegisterClick}
                  className="w-full mx-2 block px-3 py-2 rounded-md text-base font-medium bg-gradient-to-r from-[#C0392B] to-[#E67E22] text-white hover:from-[#E67E22] hover:to-[#C0392B] text-center group relative"
                >
                  Sign up
                  <CowrieShell />
                </button>
              </div>
            )}

            {isAuthenticated && (
              <div className="mt-3 px-2 space-y-1">
                <button
                  onClick={handleDashboardClick}
                  className="block w-full px-3 py-2 rounded-md text-base font-medium text-[#1A3C34] hover:text-[#C0392B] hover:bg-[#E67E22]/20 text-left"
                >
                  Dashboard
                </button>
                <Link
                  to="/admin/couple-settings"
                  className="block px-3 py-2 rounded-md text-base font-medium text-[#1A3C34] hover:text-[#C0392B] hover:bg-[#E67E22]/20"
                >
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full px-3 py-2 rounded-md text-base font-medium text-[#1A3C34] hover:text-[#C0392B] hover:bg-[#E67E22]/20 text-left"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;